import { useState } from "react";
import PropTypes from "prop-types";
import { Loader } from "components/Loader";
import { getPlanShortName } from "utils";
import { BILLING_INTERVAL } from "utils/constants";
import {
  ConfirmDialogContainer,
  Title,
  Body,
  BillingBox,
  Actions,
  DangerButton,
  SecondaryButton,
} from "../shared/ConfirmDialog.styles";

export const DowngradeConfirmModal = ({
  show,
  onHide,
  onConfirm,
  plan,
  currentPlanName,
  periodEnd,
  duration,
  isFreeDowngrade = false,
  isIntervalSwitch = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onHide();
  };

  const isYearly = duration === BILLING_INTERVAL.YEARLY;
  const shortName = getPlanShortName(plan?.name);
  const intervalLabel = isYearly ? "year" : "month";
  const intervalWord = isYearly ? "yearly" : "monthly";
  const intervalDisplay = isYearly ? "Yearly" : "Monthly";
  const oppositeWord = isYearly ? "monthly" : "yearly";
  const price = plan?.price;

  // Copy differs by mode: interval switch (same tier) / downgrade-to-Free / tier downgrade.
  const title = isIntervalSwitch
    ? `Switch to ${intervalWord} billing?`
    : isFreeDowngrade
    ? "Downgrade to the Free plan?"
    : `Downgrade to the ${shortName} plan?`;

  const body = isIntervalSwitch ? (
    <>
      You&apos;ll keep {currentPlanName} until {periodEnd}. On that date your
      billing switches to ${price} USD / {intervalLabel}.
    </>
  ) : isFreeDowngrade ? (
    <>
      Your downgrade to the Free plan is scheduled for {periodEnd}. You&apos;ll
      keep {currentPlanName} and all its features until then.
    </>
  ) : (
    <>
      You&apos;ll keep {currentPlanName} and all its features until {periodEnd}.
      On that date your plan changes to {plan?.name} and you&apos;ll be billed $
      {price} USD / {intervalLabel} going forward.
    </>
  );

  const keepLabel = isIntervalSwitch
    ? `Keep ${oppositeWord} billing`
    : `Keep your ${getPlanShortName(currentPlanName)} plan`;

  const confirmLabel = isIntervalSwitch
    ? "Switch plan"
    : isFreeDowngrade
    ? "Cancel plan"
    : "Downgrade plan";

  return (
    <ConfirmDialogContainer show={show} onHide={handleClose} backdrop="static">
      <Title>{title}</Title>

      <Body>{body}</Body>

      {!isFreeDowngrade && (
        <BillingBox>
          <div className="billing-left">
            <span className="billing-plan">
              {plan?.name} {intervalDisplay}
            </span>
            <span className="billing-date">Billing starts {periodEnd}</span>
          </div>
          <span className="billing-price">
            ${price} USD / {intervalLabel}
          </span>
        </BillingBox>
      )}

      <Actions>
        <SecondaryButton
          type="button"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {keepLabel}
        </SecondaryButton>
        <DangerButton
          type="button"
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting && <Loader className="btn-loader" variant="light" />}
          {confirmLabel}
        </DangerButton>
      </Actions>
    </ConfirmDialogContainer>
  );
};

DowngradeConfirmModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  plan: PropTypes.object,
  currentPlanName: PropTypes.string,
  periodEnd: PropTypes.string,
  duration: PropTypes.string,
  isFreeDowngrade: PropTypes.bool,
  isIntervalSwitch: PropTypes.bool,
};
